import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Gift, Check, Download, Ticket, Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_CHANCES = 3;

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/verse.clinic/',
  instagram: 'https://instagram.com/verseclinic.my/',
};

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

/* ───── Confetti ───── */
const Confetti = ({ trigger }) => {
  const [particles, setParticles] = useState([]);

  const burst = useCallback(() => {
    const colors = ['#d6a84a', '#e8c36a', '#c9972f', '#f5dfa0', '#ffffff'];
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: Date.now() + i,
      x: 50 + (Math.random() - 0.5) * 20,
      y: 40,
      color: colors[Math.floor(Math.random() * colors.length)],
      angle: Math.random() * 360,
      speed: 2 + Math.random() * 4,
      drift: (Math.random() - 0.5) * 6,
      size: 4 + Math.random() * 6,
      delay: Math.random() * 300,
      shape: Math.random() > 0.5 ? 'circle' : 'rect',
    }));
    setParticles(newParticles);
    setTimeout(() => setParticles([]), 2500);
  }, []);

  useEffect(() => {
    if (trigger > 0) burst();
  }, [trigger, burst]);

  if (!particles.length) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[10001]" aria-hidden="true">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.shape === 'circle' ? p.size : p.size * 0.6,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
            opacity: 0,
            animation: `confetti-fall 2s ease-out ${p.delay}ms forwards`,
            '--drift': `${p.drift * 40}px`,
            '--speed': `${p.speed * 100}px`,
            '--rotate': `${p.angle + Math.random() * 360}deg`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { opacity: 1; transform: translate(0, 0) rotate(0deg) scale(1); }
          100% { opacity: 0; transform: translate(var(--drift), var(--speed)) rotate(var(--rotate)) scale(0.3); }
        }
      `}</style>
    </div>
  );
};

/* ───── Mini Confetti (for social confirms) ───── */
const MiniConfetti = ({ active }) => {
  if (!active) return null;
  const colors = ['#d6a84a', '#e8c36a', '#f5dfa0'];
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl" aria-hidden="true">
      {Array.from({ length: 12 }, (_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${30 + Math.random() * 40}%`,
            top: `${20 + Math.random() * 40}%`,
            width: 4 + Math.random() * 4,
            height: 4 + Math.random() * 4,
            backgroundColor: colors[i % 3],
            borderRadius: Math.random() > 0.5 ? '50%' : '1px',
            animation: `mini-burst 0.8s ease-out ${i * 40}ms forwards`,
            opacity: 0,
          }}
        />
      ))}
      <style>{`
        @keyframes mini-burst {
          0% { opacity: 1; transform: scale(0) translate(0, 0); }
          50% { opacity: 1; transform: scale(1.2) translate(${Math.random() > 0.5 ? '' : '-'}${10 + Math.random() * 20}px, -${10 + Math.random() * 20}px); }
          100% { opacity: 0; transform: scale(0.5) translate(${Math.random() > 0.5 ? '' : '-'}${20 + Math.random() * 30}px, ${10 + Math.random() * 20}px); }
        }
      `}</style>
    </div>
  );
};

/* ───── Stacked Tickets ───── */
const TicketStack = ({ count }) => (
  <div className="relative h-16 w-24 sm:h-24 sm:w-32 mx-auto my-3 sm:my-4">
    {[...Array(MAX_CHANCES)].map((_, i) => (
      <div
        key={i}
        className="absolute left-1/2 rounded-lg transition-all duration-700"
        style={{
          width: 'min(80px, 22vw)',
          height: 'min(50px, 14vw)',
          transform: `translateX(-50%) translateY(${i * -6}px) rotate(${(i - 1) * 5}deg) scale(${i < count ? 1 : 0.95})`,
          backgroundColor: i < count ? 'rgba(214,168,74,0.15)' : 'rgba(255,255,255,0.02)',
          border: `1.5px solid ${i < count ? 'rgba(214,168,74,0.5)' : 'rgba(255,255,255,0.05)'}`,
          zIndex: i,
          bottom: 0,
          opacity: i < count ? 1 : 0.3,
          boxShadow: i < count ? '0 4px 12px rgba(214,168,74,0.1)' : 'none',
        }}
      >
        {i < count && (
          <div className="flex items-center justify-center h-full">
            <Ticket className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: 'var(--vc-gold)' }} />
          </div>
        )}
      </div>
    ))}
  </div>
);

/* ───── Progress Bar ───── */
const ChanceBar = ({ total, social, label }) => (
  <div className="w-full mx-auto mb-4 sm:mb-6">
    <div className="flex items-center justify-between mb-1.5 sm:mb-2">
      <span className="text-[11px] sm:text-xs font-medium" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>{label}</span>
      <span className="text-base sm:text-lg font-bold" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>{total}/{MAX_CHANCES}</span>
    </div>
    <div className="w-full h-2.5 sm:h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
      <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{
        width: `${(total / MAX_CHANCES) * 100}%`,
        background: 'linear-gradient(90deg, #c9972f, #d6a84a, #e8c36a)',
      }} />
    </div>
    <div className="flex gap-1 justify-center mt-1.5 sm:mt-2">
      {['Base', social.facebook ? 'Facebook' : null, social.instagram ? 'Instagram' : null]
        .filter(Boolean)
        .map((label) => (
          <span key={label} className="text-[10px] px-2 py-0.5 rounded-full" style={{
            backgroundColor: 'rgba(214,168,74,0.1)',
            color: 'var(--vc-gold)',
            fontFamily: 'Inter, sans-serif',
          }}>{label}</span>
        ))}
    </div>
  </div>
);

/* ───── Card Generator (returns canvas) ───── */
const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

const generateCardCanvas = async (ticket, chances, name) => {
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#071525';
  ctx.fillRect(0, 0, 800, 480);

  // Outer gold border
  ctx.strokeStyle = '#d6a84a';
  ctx.lineWidth = 2;
  ctx.strokeRect(20, 20, 760, 440);

  // Inner subtle border
  ctx.strokeStyle = 'rgba(214,168,74,0.15)';
  ctx.lineWidth = 1;
  ctx.strokeRect(30, 30, 740, 420);

  // Subtle corner accents (top-left & bottom-right)
  ctx.strokeStyle = 'rgba(214,168,74,0.4)';
  ctx.lineWidth = 2;
  [[20, 20, 50, 0, 0, 50], [780, 460, -50, 0, 0, -50]].forEach(([x, y, dx1, dy1, dx2, dy2]) => {
    ctx.beginPath();
    ctx.moveTo(x + dx1, y + dy1);
    ctx.lineTo(x, y);
    ctx.lineTo(x + dx2, y + dy2);
    ctx.stroke();
  });

  ctx.textAlign = 'center';

  // Logo
  try {
    const logo = await loadImage('/images/logo.png');
    const logoW = 160;
    const logoH = logoW * (logo.height / logo.width);
    ctx.drawImage(logo, (800 - logoW) / 2, 48, logoW, logoH);
  } catch {
    ctx.fillStyle = '#d6a84a';
    ctx.font = 'bold 22px Inter, sans-serif';
    ctx.fillText('VERSE CLINIC', 400, 75);
  }

  // "LUCKY DRAW ENTRY" label with dashes
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.font = '600 10px Inter, sans-serif';
  ctx.letterSpacing = '3px';
  ctx.fillText('──  LUCKY DRAW ENTRY  ──', 400, 128);

  // HERO: Ticket number
  ctx.save();
  ctx.shadowColor = 'rgba(214,168,74,0.35)';
  ctx.shadowBlur = 40;
  ctx.fillStyle = '#d6a84a';
  ctx.font = 'bold 80px Inter, sans-serif';
  ctx.fillText(ticket, 400, 230);
  ctx.restore();

  // Name
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font = '16px Inter, sans-serif';
  ctx.fillText(name, 400, 265);

  // Chances — small, bottom area
  const chanceY = 330;
  const dotR = 5;
  const dotGap = 18;
  const dotsW = MAX_CHANCES * dotGap;
  const dotsStartX = 400 - dotsW / 2 + dotGap / 2 - 40;
  for (let i = 0; i < MAX_CHANCES; i++) {
    ctx.beginPath();
    ctx.arc(dotsStartX + i * dotGap, chanceY, dotR, 0, Math.PI * 2);
    if (i < chances) {
      ctx.fillStyle = '#d6a84a';
      ctx.fill();
    } else {
      ctx.strokeStyle = 'rgba(214,168,74,0.3)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = '13px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(`${chances}/${MAX_CHANCES} Chances`, dotsStartX + MAX_CHANCES * dotGap + 6, chanceY + 4.5);
  ctx.textAlign = 'center';

  // Divider line
  ctx.strokeStyle = 'rgba(214,168,74,0.15)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(120, 370);
  ctx.lineTo(680, 370);
  ctx.stroke();

  // Location — two lines
  ctx.fillStyle = 'rgba(214,168,74,0.5)';
  ctx.font = '13px Inter, sans-serif';
  ctx.fillText('Grand Opening  ·  DC Mall, Damansara City  ·  Kuala Lumpur', 400, 400);

  // Website
  ctx.fillStyle = 'rgba(214,168,74,0.3)';
  ctx.font = '11px Inter, sans-serif';
  ctx.fillText('verseclinic.com', 400, 430);

  return canvas;
};

const canNativeShare = () => typeof navigator !== 'undefined' && !!navigator.share && !!navigator.canShare;

const shareCard = async (ticket, chances, name) => {
  const canvas = await generateCardCanvas(ticket, chances, name);
  const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
  const file = new File([blob], `${ticket}-lucky-draw.png`, { type: 'image/png' });
  if (canNativeShare() && navigator.canShare({ files: [file] })) {
    await navigator.share({ title: `VERSE CLINIC Lucky Draw - ${ticket}`, files: [file] });
  }
};

const downloadCard = async (ticket, chances, name) => {
  const canvas = await generateCardCanvas(ticket, chances, name);
  const link = document.createElement('a');
  link.download = `${ticket}-lucky-draw.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
};

/* ═══════════════════════════════════════════════════
   MAIN MODAL COMPONENT
   ═══════════════════════════════════════════════════ */
export const LuckyDrawModal = ({ isOpen, onClose, ticket, name, phone }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('reveal'); // reveal → challenge → done
  const [social, setSocial] = useState({ facebook: false, instagram: false });
  const [total, setTotal] = useState(1);
  const [confettiBurst, setConfettiBurst] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);
  const [loading, setLoading] = useState(null);
  const [fbConfetti, setFbConfetti] = useState(false);
  const [igConfetti, setIgConfetti] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [actionError, setActionError] = useState('');
  const [cardImageUrl, setCardImageUrl] = useState(null);
  const [showCardPreview, setShowCardPreview] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfettiBurst((v) => v + 1);
      setRevealed(false);
      setTimeout(() => setRevealed(true), 100);
      setTimeout(() => setPhase('challenge'), 2200);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Generate/refresh card image when modal is open or chances change
  useEffect(() => {
    if (isOpen && ticket && name) {
      generateCardCanvas(ticket, total, name).then((canvas) => {
        setCardImageUrl(canvas.toDataURL('image/png'));
      });
    }
  }, [isOpen, ticket, total, name]);

  const handleSaveCard = () => {
    setShowCardPreview(true);
  };

  const handleShare = async () => {
    try {
      await shareCard(ticket, total, name);
    } catch {
      downloadCard(ticket, total, name);
    }
  };

  const handleDownload = () => {
    downloadCard(ticket, total, name);
  };

  const handleSocialClick = (key) => {
    window.open(SOCIAL_LINKS[key], '_blank');
    setPendingAction(key);
    setActionError('');
  };

  const confirmAction = async () => {
    if (!pendingAction || !phone) return;
    setLoading(pendingAction);
    setActionError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/lucky-draw/social-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, action: pendingAction }),
      });
      const data = await res.json();
      if (data.ok) {
        setSocial((prev) => ({ ...prev, [pendingAction]: true }));
        setTotal(data.total_chances);
        if (pendingAction === 'facebook') setFbConfetti(true);
        if (pendingAction === 'instagram') setIgConfetti(true);
        setConfettiBurst((v) => v + 1);
        setTimeout(() => { setFbConfetti(false); setIgConfetti(false); }, 1000);
        if (data.total_chances >= MAX_CHANCES) {
          setTimeout(() => setPhase('done'), 800);
        }
      } else {
        setActionError(data.message || t('lucky_draw.action_error'));
      }
    } catch {
      setActionError(t('lucky_draw.connection_error'));
    }
    setLoading(null);
    setPendingAction(null);
  };

  if (!isOpen) return null;

  return (
    <>
      <Confetti trigger={confettiBurst} />
      <div
        data-testid="lucky-draw-modal-overlay"
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          data-testid="lucky-draw-modal"
          className="relative w-full max-w-md rounded-[20px] sm:rounded-[24px] overflow-hidden"
          style={{
            backgroundColor: 'rgba(7,21,37,0.98)',
            border: '1px solid rgba(214,168,74,0.2)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 60px rgba(214,168,74,0.08)',
            maxHeight: '88vh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Close button */}
          <button
            data-testid="lucky-draw-modal-close"
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-[10002] w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'var(--vc-fg-muted)' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.18)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
          >
            <X className="w-4 h-4" />
          </button>

          <div className="px-5 pt-5 pb-5 sm:px-8 sm:pt-8 sm:pb-8">
            {/* Badge */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full mb-4 sm:mb-5" style={{
                backgroundColor: 'rgba(214,168,74,0.1)',
                border: '1px solid rgba(214,168,74,0.25)',
              }}>
                <Gift className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: 'var(--vc-gold)' }} />
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
                  {t('lucky_draw.badge')}
                </span>
              </div>

              {/* Ticket Number — Animated Reveal */}
              <div style={{
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'scale(1) translateY(0)' : 'scale(0.7) translateY(20px)',
                transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
              }}>
                <p className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight" style={{
                  color: 'var(--vc-gold)',
                  fontFamily: 'Inter, sans-serif',
                  textShadow: '0 0 40px rgba(214,168,74,0.3)',
                }}>
                  {ticket}
                </p>
                <p className="text-xs sm:text-sm mt-1.5 sm:mt-2" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
                  {name}
                </p>
              </div>
            </div>

            {/* Tickets + Progress */}
            <div style={{
              opacity: revealed ? 1 : 0,
              transition: 'opacity 0.6s ease 0.4s',
            }}>
              <TicketStack count={total} />
              <ChanceBar total={total} social={social} label={t('lucky_draw.your_chances')} />
            </div>

            {/* Social Challenge */}
            {(phase === 'challenge' || phase === 'done') && (
              <div style={{
                opacity: 1,
                animation: 'fadeSlideUp 0.5s ease-out',
              }}>
                {total < MAX_CHANCES ? (
                  <>
                    <div className="text-center mb-3 sm:mb-5">
                      <p className="text-sm sm:text-base font-semibold" style={{
                        color: 'var(--vc-fg)',
                        fontFamily: 'Inter, sans-serif',
                      }}>
                        {t('lucky_draw.want_better_odds')}
                      </p>
                      <p className="text-[11px] sm:text-xs mt-1" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
                        {t('lucky_draw.follow_extra')}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-3 sm:mb-4">
                      {/* Facebook Card */}
                      <div className="relative rounded-xl overflow-hidden transition-all duration-300" style={{
                        backgroundColor: social.facebook ? 'rgba(214,168,74,0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1.5px solid ${social.facebook ? 'rgba(214,168,74,0.35)' : 'rgba(255,255,255,0.08)'}`,
                      }}>
                        <MiniConfetti active={fbConfetti} />
                        <button
                          data-testid="social-action-facebook"
                          onClick={() => !social.facebook && handleSocialClick('facebook')}
                          disabled={social.facebook}
                          className="w-full p-3 sm:p-4 flex flex-col items-center gap-2 sm:gap-3 transition-all duration-200"
                          style={{ cursor: social.facebook ? 'default' : 'pointer' }}
                        >
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300" style={{
                            backgroundColor: social.facebook ? 'rgba(214,168,74,0.15)' : 'rgba(255,255,255,0.06)',
                          }}>
                            {social.facebook
                              ? <Check className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: 'var(--vc-gold)' }} />
                              : <FacebookIcon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#1877F2' }} />
                            }
                          </div>
                          <span className="text-[11px] sm:text-xs font-medium" style={{
                            color: social.facebook ? 'var(--vc-gold)' : 'var(--vc-fg-muted)',
                            fontFamily: 'Inter, sans-serif',
                          }}>
                            {social.facebook ? t('lucky_draw.done_social') : 'Facebook'}
                          </span>
                          {!social.facebook && (
                            <span className="text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{
                              backgroundColor: 'rgba(214,168,74,0.12)',
                              color: 'var(--vc-gold)',
                            }}>{t('lucky_draw.plus_chance')}</span>
                          )}
                        </button>
                      </div>

                      {/* Instagram Card */}
                      <div className="relative rounded-xl overflow-hidden transition-all duration-300" style={{
                        backgroundColor: social.instagram ? 'rgba(214,168,74,0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1.5px solid ${social.instagram ? 'rgba(214,168,74,0.35)' : 'rgba(255,255,255,0.08)'}`,
                      }}>
                        <MiniConfetti active={igConfetti} />
                        <button
                          data-testid="social-action-instagram"
                          onClick={() => !social.instagram && handleSocialClick('instagram')}
                          disabled={social.instagram}
                          className="w-full p-3 sm:p-4 flex flex-col items-center gap-2 sm:gap-3 transition-all duration-200"
                          style={{ cursor: social.instagram ? 'default' : 'pointer' }}
                        >
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300" style={{
                            backgroundColor: social.instagram ? 'rgba(214,168,74,0.15)' : 'rgba(255,255,255,0.06)',
                          }}>
                            {social.instagram
                              ? <Check className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: 'var(--vc-gold)' }} />
                              : <InstagramIcon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#E4405F' }} />
                            }
                          </div>
                          <span className="text-[11px] sm:text-xs font-medium" style={{
                            color: social.instagram ? 'var(--vc-gold)' : 'var(--vc-fg-muted)',
                            fontFamily: 'Inter, sans-serif',
                          }}>
                            {social.instagram ? t('lucky_draw.done_social') : 'Instagram'}
                          </span>
                          {!social.instagram && (
                            <span className="text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{
                              backgroundColor: 'rgba(214,168,74,0.12)',
                              color: 'var(--vc-gold)',
                            }}>{t('lucky_draw.plus_chance')}</span>
                          )}
                        </button>
                      </div>
                    </div>

                    {pendingAction && (
                      <button
                        data-testid="confirm-social-modal"
                        onClick={confirmAction}
                        disabled={!!loading}
                        className="w-full h-10 sm:h-11 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 active:scale-[0.98] mb-1"
                        style={{
                          backgroundColor: 'var(--vc-gold)',
                          color: 'var(--vc-ink)',
                          fontFamily: 'Inter, sans-serif',
                          opacity: loading ? 0.7 : 1,
                        }}
                      >
                        {loading ? t('lucky_draw.confirming') : t('lucky_draw.confirm_follow')}
                      </button>
                    )}

                    {actionError && (
                      <p data-testid="social-action-error" className="text-xs text-center mb-2 sm:mb-3" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>
                        {actionError}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="text-center mb-3 sm:mb-4" style={{ animation: 'fadeSlideUp 0.5s ease-out' }}>
                    <p className="text-base sm:text-lg font-bold" style={{ color: 'var(--vc-gold)', fontFamily: 'Inter, sans-serif' }}>
                      {t('lucky_draw.all_set')}
                    </p>
                    <p className="text-[11px] sm:text-xs mt-1" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
                      {t('lucky_draw.max_chances')}
                    </p>
                  </div>
                )}

                {/* Card Preview + Save/Share */}
                <div className="pt-3 sm:pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  {showCardPreview && cardImageUrl ? (
                    <div className="mb-3 sm:mb-4" style={{ animation: 'fadeSlideUp 0.3s ease-out' }}>
                      <p className="text-[11px] sm:text-xs text-center mb-2" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}>
                        {t('lucky_draw.longpress_hint')}
                      </p>
                      <img
                        data-testid="lucky-draw-card-image"
                        src={cardImageUrl}
                        alt={`Lucky Draw Card ${ticket}`}
                        className="w-full rounded-lg"
                        style={{ border: '1px solid rgba(214,168,74,0.15)' }}
                      />
                      <div className="flex gap-2 mt-2.5">
                        {canNativeShare() && (
                          <button
                            data-testid="share-lucky-draw-card"
                            onClick={handleShare}
                            className="flex-1 flex items-center justify-center gap-1.5 h-9 sm:h-10 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 active:scale-[0.98]"
                            style={{
                              backgroundColor: 'var(--vc-gold)',
                              color: 'var(--vc-ink)',
                              fontFamily: 'Inter, sans-serif',
                            }}
                          >
                            <Share2 className="w-3.5 h-3.5" />
                            {t('lucky_draw.share')}
                          </button>
                        )}
                        <button
                          data-testid="download-lucky-draw-card"
                          onClick={handleDownload}
                          className="flex-1 flex items-center justify-center gap-1.5 h-9 sm:h-10 rounded-xl text-xs sm:text-sm font-medium border transition-all duration-200 active:scale-[0.98]"
                          style={{
                            borderColor: 'rgba(214,168,74,0.3)',
                            color: 'var(--vc-gold)',
                            backgroundColor: 'transparent',
                            fontFamily: 'Inter, sans-serif',
                          }}
                        >
                          <Download className="w-3.5 h-3.5" />
                          {t('lucky_draw.download')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      data-testid="save-lucky-draw-card"
                      onClick={handleSaveCard}
                      className="w-full flex items-center justify-center gap-2 h-10 sm:h-11 rounded-xl text-xs sm:text-sm font-medium border transition-all duration-200 active:scale-[0.98] mb-2"
                      style={{
                        borderColor: 'rgba(214,168,74,0.3)',
                        color: 'var(--vc-gold)',
                        backgroundColor: 'transparent',
                        fontFamily: 'Inter, sans-serif',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.06)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {t('lucky_draw.save_card')}
                    </button>
                  )}

                  <button
                    data-testid="lucky-draw-modal-done"
                    onClick={onClose}
                    className="w-full h-9 sm:h-10 rounded-xl text-xs sm:text-sm font-medium transition-colors duration-200"
                    style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter, sans-serif' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--vc-fg)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--vc-fg-muted)'}
                  >
                    {t('lucky_draw.done')}
                  </button>
                </div>

                <p className="text-[10px] text-center mt-2 sm:mt-3" style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter, sans-serif' }}>
                  {t('lucky_draw.footer_notice')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};
